import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useProperty } from "@/contexts/PropertyContext";
import {
  ClipboardCheck, Timer, Camera, ChevronRight, Trophy, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

/**
 * R141 — Admin/PM Tech Leaderboard.
 *
 * Cross-team roll-up of the same three metrics `/tech/pace` reports —
 * ranked by tickets closed with tie-break on faster average accept.
 * Property scope threads through via the property switcher context.
 */
function fmtDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function currentMonthYYYYMM(d = new Date()) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  return currentMonthYYYYMM(new Date(Date.UTC(y, m - 1 + delta, 1)));
}

const HeadlineTile = ({ label, value, icon: Icon, accent }) => (
  <Card className="p-4 bg-white/[0.03] border-white/10">
    <div className="flex items-center justify-between mb-2">
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <div className={`text-3xl font-semibold tabular-nums ${accent}`}>{value}</div>
  </Card>
);

export default function TechLeaderboardPage() {
  const { propertyId } = useProperty();
  const [month, setMonth] = useState(currentMonthYYYYMM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nowYm = currentMonthYYYYMM();
  const atCurrent = month === nowYm;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (propertyId && propertyId !== "all") params.property_id = propertyId;
      const { data } = await api.get("/admin/tech-pace", { params });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load leaderboard");
      setData(null);
    } finally { setLoading(false); }
  }, [month, propertyId]);

  useEffect(() => { load(); }, [load]);

  const team = data?.team || {};
  const rows = data?.rows || [];

  return (
    <div data-testid="tech-leaderboard-page">
      <PageHeader
        eyebrow="Team performance"
        title="Tech leaderboard"
        subtitle="Monthly tickets closed, accept time, and photo attach rate — ranked."
      />

      {/* Month picker */}
      <div className="flex items-center gap-2 mb-4">
        <Button size="sm" variant="outline"
                onClick={() => setMonth(shiftMonth(month, -1))}
                className="border-white/10"
                data-testid="leaderboard-prev-month">
          <ChevronRight className="h-3.5 w-3.5 rotate-180 mr-1" /> Prev
        </Button>
        <div className="text-sm tabular-nums font-medium min-w-[80px] text-center"
             data-testid="leaderboard-month-label">
          {month}
        </div>
        <Button size="sm" variant="outline" disabled={atCurrent}
                onClick={() => setMonth(shiftMonth(month, 1))}
                className="border-white/10 disabled:opacity-40"
                data-testid="leaderboard-next-month">
          Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                className="text-white/70 hover:text-white"
                data-testid="leaderboard-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Team headline */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <HeadlineTile label="Tickets closed (team)" value={team.tickets_closed ?? 0}
                      icon={ClipboardCheck} accent="text-emerald-300" />
        <HeadlineTile label="Avg accept time (team)"
                      value={fmtDuration(team.avg_accept_seconds)}
                      icon={Timer} accent="text-sky-300" />
        <HeadlineTile label="Photo attach rate (team)"
                      value={`${team.photo_attach_rate ?? 0}%`}
                      icon={Camera} accent="text-amber-300" />
      </div>

      {/* Leaderboard */}
      <Card className="border-white/10 bg-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-orange-400" />
          <div className="text-sm font-semibold">Techs — {data?.tech_count ?? 0} on the board</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-white/50 text-center"
               data-testid="leaderboard-empty">
            No tech activity for this month yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-white/60">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">Tech</th>
                  <th className="px-3 py-2 text-right">Closed</th>
                  <th className="px-3 py-2 text-right">Avg accept</th>
                  <th className="px-3 py-2 text-right">Photo rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.user_id}
                      className="border-t border-white/5"
                      data-testid={`leaderboard-row-${r.user_id}`}>
                    <td className="px-3 py-2.5 text-white/50 tabular-nums">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <div className="text-white">{r.user_name || "—"}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">
                        {r.role}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className="font-medium">{r.tickets_closed}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-white/80">
                      {fmtDuration(r.avg_accept_seconds)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <Badge variant="outline" className={
                        r.photo_attach_rate >= 80
                          ? "border-emerald-500/40 text-emerald-300"
                          : r.photo_attach_rate >= 50
                            ? "border-amber-500/40 text-amber-300"
                            : "border-white/10 text-white/50"
                      }>
                        {r.photo_attach_rate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
